import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './App.css';
import "bootstrap/dist/css/bootstrap.min.css";
import "./Assets/Styles/App.scss";
import AppRoutes from "./Routes/Routes";
import Navbar from "./Layout/Navbar";
import WelcomeNavbar from "./Layout/WelcomeNavbar";
import useTheme from "./hooks/useTheme";
import loading from "./Assets/Animations/loading.gif";
import CryptoKeyContext from "./Context/CryptoKey/CryptoKeyContext";
import { importKeyFromBase64 } from "./Utils/crypto";
import { userApi } from "./Utils/API";
import { GRAPHQL_PROXY } from "./Utils/Endpoints";

// Custom hook for robust intervals
function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
  }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

function App() {
  useTheme(); // Hook to apply the theme class to the body
  const { instance, accounts } = useMsal();
  const customerIsAuthenticated = accounts.length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(true);

  const lastActivityTimeRef = useRef(Date.now());
  const tokenRefreshIntervalRef = useRef(null);
  const [isUserActive, setIsUserActive] = useState(true);

  // AES-GCM encryption key — fetched from CMS siteSettings after authentication.
  // The CryptoKey object is held ONLY in React state (JS heap memory).
  // It is NEVER written to localStorage, sessionStorage, or any other store.
  const [cryptoKey, setCryptoKey] = useState(null);
  const [keyVersion, setKeyVersion] = useState(null);
  const cryptoKeyFetchedRef = useRef(false);

  const handleUserActivity = useCallback(() => {
  lastActivityTimeRef.current = Date.now();
    if (!isUserActive) {
      setIsUserActive(true);
      console.log("User now active");
    }
  }, [isUserActive]);

  const handleGetUserData = useCallback(async () => {
    if (!isUserActive || accounts.length === 0) {
      console.log("Token refresh skipped: User is inactive or no account is available.");
      return;
    }
    console.log("Attempting to refresh token...");
    try {
      const account = accounts[0];
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["https://newcloudsynnex.onmicrosoft.com/tasks-api/tasks.read"],
        account: account,
forceRefresh: true,
      });
      console.log("Token successfully refreshed:", tokenResponse.accessToken);
      localStorage.setItem("access_token", tokenResponse.accessToken);
      lastActivityTimeRef.current = Date.now(); // Reset inactivity timer on successful refresh
    } catch (err) {
      console.error("Token refresh failed:", err);
    }
  }, [instance, isUserActive, accounts]);

  /**
 * Fetch the AES-GCM 256-bit encryption key from Orchard Core CMS siteSettings.
 * This runs once after the user is confirmed authenticated.
   * The raw Base64 key from CMS is immediately imported via WebCrypto into a
   * non-extractable CryptoKey object and stored in React state only.
   */
  const fetchEncryptionKey = useCallback(async () => {
    if (cryptoKeyFetchedRef.current) return; // Only fetch once per session
    cryptoKeyFetchedRef.current = true;

    const token_type = localStorage.getItem("token_type");
    const access_token = localStorage.getItem("access_token");

    const query = `{ siteSettings { keyVersion encryptionKeyBase64 } }`;

    try {
      const resp = await userApi.post(
        GRAPHQL_PROXY,
        { query },
      {
          headers: {
     "Content-Type": "application/json",
         Authorization: `${token_type} ${access_token}`,
       },
        }
      );

      const settings = resp?.data?.data?.siteSettings;
      const entry = Array.isArray(settings) ? settings[0] : settings;

      if (entry?.encryptionKeyBase64) {
   const importedKey = await importKeyFromBase64(entry.encryptionKeyBase64);
        setCryptoKey(importedKey);
        setKeyVersion(entry.keyVersion || 'v1');
        console.log("Encryption key loaded successfully (keyVersion:", entry.keyVersion || 'v1', ")");
      } else {
     console.warn("siteSettings.encryptionKeyBase64 not found in CMS — encrypted token storage will be unavailable.");
      }
    } catch (err) {
      // Non-fatal: if CMS is down or key is not yet configured, the app continues
      // to function normally — the Authorization prefill simply won't be available
  // until the user manually calls Create Access Token.
   console.warn("Could not fetch encryption key from CMS:", err?.message || err);
}
  }, []);

  // Use the robust useInterval hook
  useInterval(
    () => {
   if (customerIsAuthenticated && isUserActive) {
        handleGetUserData();
    }
    },
    3 * 60 * 60 * 1000 // 3 hours
  );

  useEffect(() => {
    AOS.init({
      duration: 1000, // Animation duration in milliseconds
      once: true, // Whether animation should happen only once - while scrolling down
    });
    instance.handleRedirectPromise().finally(() => {
      setIsRedirecting(false);
    });
  }, [instance]);

  useEffect(() => {
    const checkUserActivity = () => {
      const currentTime = Date.now();
      const inactiveTime = currentTime - lastActivityTimeRef.current;
    const inactiveSeconds = Math.round(inactiveTime / 1000);
      console.log(
 `User active: ${isUserActive}. Inactive for: ${inactiveSeconds} seconds.`
    );

   // If inactive for more than 16 minutes, log out
      if (inactiveTime > 4 * 60 * 60 * 1000) {
        if (isUserActive) {
        setIsUserActive(false);
   console.log("User has become inactive. Logging out.");
          if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
 tokenRefreshIntervalRef.current = null;
          }
          localStorage.removeItem("navigation_link");
          localStorage.clear();
          navigate("/");
          instance.logoutRedirect();
        }
      }
    };

    const activityInterval = setInterval(checkUserActivity, 60 * 1000); // Check every 30 seconds

    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);

    return () => {
      clearInterval(activityInterval);
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
   }
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [handleUserActivity, instance, isUserActive, navigate]);

  useEffect(() => {
    if (customerIsAuthenticated && isUserActive) {
      handleGetUserData(); // Get token on initial load
    }
  }, [customerIsAuthenticated, isUserActive, handleGetUserData]);

  // Fetch the CMS encryption key once the user is authenticated.
  // We wait until isRedirecting is false so that localStorage.access_token
  // has been populated by the MSAL token refresh above.
  useEffect(() => {
if (customerIsAuthenticated && !isRedirecting) {
      fetchEncryptionKey();
    }
  }, [customerIsAuthenticated, isRedirecting, fetchEncryptionKey]);

  // Reset the fetch flag on logout so the next login re-fetches the key
  useEffect(() => {
    if (!customerIsAuthenticated) {
      cryptoKeyFetchedRef.current = false;
      setCryptoKey(null);
setKeyVersion(null);
    }
  }, [customerIsAuthenticated]);

  if (isRedirecting) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <img src={loading} alt="Loading..." />
      </div>
    );
  }

  return (
    // CryptoKeyContext.Provider makes the AES-GCM CryptoKey available to all
// authenticated components (RightPanel, ApiReference) without prop drilling.
    // The key is null when the user is unauthenticated or the CMS fetch hasn't
    // completed yet — consumers handle null gracefully.
    <CryptoKeyContext.Provider value={{ cryptoKey, keyVersion, isReady: !!cryptoKey }}>
      <div className="App">
        {location.pathname !== '/unauthorised-access' && (
          <>
<AuthenticatedTemplate>
              <Navbar />
        </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
              <WelcomeNavbar />
      </UnauthenticatedTemplate>
  </>
   )}
 <AppRoutes />
  </div>
    </CryptoKeyContext.Provider>
  );
}

export default App;
